import { create } from 'zustand'
import type { Account, CreateAccountInput, ApiResponse } from '../types'
import { api } from '../lib/api'

interface AccountState {
  accounts: Account[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchAccounts: () => Promise<void>
  addAccount: (input: CreateAccountInput) => Promise<ApiResponse<Account>>
  updateAccount: (id: number, updates: Partial<Account>) => Promise<ApiResponse<Account>>
  deleteAccount: (id: number) => Promise<ApiResponse<void>>
  toggleAccount: (id: number) => Promise<void>
}

export const useAccountStore = create<AccountState>((set, get) => ({
  accounts: [],
  isLoading: false,
  error: null,

  fetchAccounts: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.get<Account[]>('/api/accounts')

      if (response.success && response.data) {
        set({ accounts: response.data, isLoading: false })
      } else {
        set({ error: response.error || 'Failed to fetch accounts', isLoading: false })
      }
    } catch (err) {
      set({ error: 'Network error', isLoading: false })
    }
  },

  addAccount: async (input) => {
    try {
      const response = await api.post<Account>('/api/accounts', input)

      if (response.success && response.data) {
        set((state) => ({ accounts: [...state.accounts, response.data!] }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  updateAccount: async (id, updates) => {
    try {
      const response = await api.put<Account>(`/api/accounts/${id}`, updates)

      if (response.success && response.data) {
        set((state) => ({
          accounts: state.accounts.map((a) =>
            a.id === id ? { ...a, ...response.data } : a
          ),
        }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  deleteAccount: async (id) => {
    try {
      const response = await api.delete<void>(`/api/accounts/${id}`)

      if (response.success) {
        set((state) => ({
          accounts: state.accounts.filter((a) => a.id !== id),
        }))
      }

      return response
    } catch (err) {
      return { success: false, error: 'Network error' }
    }
  },

  toggleAccount: async (id) => {
    const account = get().accounts.find((a) => a.id === id)
    if (account) {
      await get().updateAccount(id, { enabled: !account.enabled })
    }
  },
}))
